import { MapData } from '@/features/maps/core/types/MapData';
import { toSolidDate } from './mapResolver';

export const STORAGE_KEY = 'shabtai_virtual_maps';
export const PREFIX = 'virt_';
export const SIM_PREFIX = `${PREFIX}sim_`;
export const VAR_PREFIX = `${PREFIX}var_`;
export const ORIG_PREFIX = `${PREFIX}orig_`;

export const isVirtualId = (id: string): boolean => {
    return id.startsWith(PREFIX) || id.startsWith('temp-');
};

/**
 * Checks if an ID represents a simulated map.
 */
export const isSimulatedId = (id: string): boolean => {
    return id.startsWith(SIM_PREFIX);
};

/**
 * Checks if an ID represents a variation map.
 */
export const isVariationId = (id: string): boolean => {
    return id.startsWith(VAR_PREFIX);
};

/**
 * Strips tool prefixes to get the root ID (the atomic anchor map).
 * Correctly handles nested virtual IDs by identifying the inner orig_ prefix.
 */
export const normalizeVirtualId = (id: string): string => {
    if (!isVirtualId(id)) return id;
    
    // Pattern: virt_[sim|var]_[ANCHOR]_[HASH]
    // If we have a tool prefix, we want the ANCHOR part (virt_orig_...)
    if (id.startsWith(SIM_PREFIX) || id.startsWith(VAR_PREFIX)) {
        const prefix = id.startsWith(SIM_PREFIX) ? SIM_PREFIX : VAR_PREFIX;
        const content = id.slice(prefix.length);
        const lastHashIdx = content.lastIndexOf('_');
        const anchorCandidate = lastHashIdx !== -1 ? content.slice(0, lastHashIdx) : content;
        
        // If the anchor is itself a virtual ID (virt_orig_...), return it fully
        return anchorCandidate.startsWith(PREFIX) ? anchorCandidate : `${PREFIX}${anchorCandidate}`;
    }
    
    return id;
};

/**
 * Deconstructs a virtual ID to extract the base ID and the name hash.
 */
export const deconstructVirtualId = (id: string): { baseId: string; paramHash?: string; decodedName?: string; type?: 'sim' | 'var' | 'orig' } | null => {
    if (!isVirtualId(id)) return null;

    let currentId = id;
    let lastHash: string | undefined;
    let lastType: 'sim' | 'var' | 'orig' | undefined;

    try {
        if (id.startsWith(SIM_PREFIX)) {
            lastType = 'sim';
            const content = id.slice(SIM_PREFIX.length);
            const last_ = content.lastIndexOf('_');
            if (last_ !== -1) {
                lastHash = content.slice(last_ + 1);
                currentId = content.slice(0, last_);
            } else {
                currentId = content;
            }
        } else if (id.startsWith(VAR_PREFIX)) {
            lastType = 'var';
            const content = id.slice(VAR_PREFIX.length);
            const last_ = content.lastIndexOf('_');
            if (last_ !== -1) {
                lastHash = content.slice(last_ + 1);
                currentId = content.slice(0, last_);
            } else {
                currentId = content;
            }
        } else if (id.startsWith(ORIG_PREFIX)) {
            lastType = 'orig';
            const core = id.slice(ORIG_PREFIX.length);
            
            if (core.startsWith('temp_')) {
                // Temporary map anchors are atomic
                currentId = id;
            } else {
                // Real map anchors strip the content hash
                const last_ = core.lastIndexOf('_');
                currentId = last_ !== -1 ? core.slice(0, last_) : core;
            }
        } else {
            return null;
        }

        let decodedName: string | undefined;
        if (lastHash) {
            try {
                /**
                 * CRITICAL AMENDMENT (100000X Detective Fix):
                 * Partial hashes are exactly 10 characters and are NOT valid Base64 payloads.
                 * Actual names are encoded fully and typically result in > 15 characters.
                 * We treat any 10-char string as a FINGERPRINT and NEVER decode it as a name.
                 */
                const isPartialHash = lastHash.length === 10 && /^[A-Za-z0-9]+$/.test(lastHash);
                
                if (!isPartialHash && /^[A-Za-z0-9+/=]+$/.test(lastHash)) {
                    decodedName = decodeURIComponent(escape(atob(lastHash)));
                } else {
                    decodedName = undefined; // It's a fingerprint, not a name
                }
            } catch {
                decodedName = undefined;
            }
        }

        return { 
          baseId: currentId, 
          paramHash: lastHash, 
          decodedName, 
          type: lastType 
        };
    } catch (e) {
        console.error('[MAP_DNA] Deconstruction crashed', e);
        return null;
    }
};

export interface VirtualMapWrapper {
    map: MapData;
    createdAt: number;
}

/**
 * Low-level accessor for raw wrapped data.
 */
const getRawStorage = (): Record<string, VirtualMapWrapper | MapData> => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    } catch (e) {
        console.error('Failed to parse virtual maps from localStorage', e);
        return {};
    }
};

/**
 * Retrieves all virtual maps from localStorage with transparent migration.
 */
export const getAllVirtualMaps = (): Record<string, MapData> => {
    const raw = getRawStorage();
    const migrated: Record<string, MapData> = {};
    let needsWriteBack = false;

    Object.entries(raw).forEach(([id, entry]) => {
        const e = entry;
        if (e && typeof e === 'object' && 'id' in e && !('map' in e)) {
            // Legacy flat format - Upgrade it
            const legacyMap = e as MapData;
            migrated[id] = legacyMap;
            raw[id] = { map: legacyMap, createdAt: Date.now() }; // Update in-place
            needsWriteBack = true;
        } else if (e && typeof e === 'object' && 'map' in e) {
            migrated[id] = e.map as MapData;
        }
    });

    if (needsWriteBack) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
    }

    return migrated;
};

/**
 * Retrieves a specific virtual map by ID.
 */
export const getVirtualMap = (id: string): MapData | null => {
    if (!isVirtualId(id)) return null;
    const raw = getRawStorage();
    const entry = raw[id];
    if (!entry) return null;
    return 'map' in entry ? entry.map : entry;
};

/**
 * Saves a virtual map to localStorage with a 24h expiration timestamp.
 */
export const saveVirtualMap = (id: string, map: MapData): void => {
    if (!isVirtualId(id)) {
        console.warn(`Attempted to save virtual map with non-prefixed ID: ${id}`);
        return;
    }

    const raw = getRawStorage();
    raw[id] = {
        map,
        createdAt: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
};

/**
 * Removes a virtual map from localStorage.
 */
export const removeVirtualMap = (id: string): void => {
    if (!isVirtualId(id)) return;
    const raw = getRawStorage();
    if (raw[id]) {
        delete raw[id];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
    }
};

/**
 * Clears all virtual maps from localStorage.
 */
export const clearAllVirtualMaps = (): void => {
    localStorage.removeItem(STORAGE_KEY);
};

/**
 * Purges virtual maps older than 24 hours.
 */
export const cleanupOldMaps = (): void => {
    const raw = getRawStorage();
    const now = Date.now();
    const TTL = 24 * 60 * 60 * 1000;
    let changed = false;

    Object.entries(raw).forEach(([id, entry]) => {
        if (entry && 'createdAt' in entry) {
            if (now - entry.createdAt > TTL) {
                delete raw[id];
                changed = true;
            }
        }
    });

    if (changed) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
    }
};

/**
 * Ensures a map has a resolvable ID (either a real UUID or a virtual ID).
 */
export const ensureVirtualMap = (id: string, map: MapData): string => {
    if (isVirtualId(id)) return id;

    const isRealUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (isRealUuid) return id;

    const contentKey = `${map.firstName}_${map.familyName || ''}_${toSolidDate(map.birthDate)}`;
    const contentHash = btoa(unescape(encodeURIComponent(contentKey))).replace(/=/g, '').slice(-10);

    const virtualId = `${PREFIX}orig_${id || 'temp'}_${contentHash}`;
    saveVirtualMap(virtualId, { ...map, id: virtualId });
    return virtualId;
};

/**
 * getRootMapId
 * Recursively deconstructs nested virtual IDs to find the absolute raw database map ID.
 */
export const getRootMapId = (id: string): string => {
    let current = id;
    while (true) {
        const decon = deconstructVirtualId(current);
        if (!decon || decon.baseId === current) break;
        current = decon.baseId;
    }
    
    let root = current;
    if (root.startsWith('virt_orig_')) {
        root = root.slice('virt_orig_'.length);
    }
    if (root.startsWith('temp_')) {
        root = root.slice('temp_'.length);
    }
    
    const last_ = root.lastIndexOf('_');
    if (last_ !== -1) {
        root = root.slice(0, last_);
    }
    return root;
};
